import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useContentProtection } from "@/hooks/useContentProtection";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import HomePage from "@/pages/HomePage";
import SchoolMaterialsPage from "@/pages/SchoolMaterialsPage";
import Dashboard from "@/pages/Dashboard";
import ChatPage from "@/pages/ChatPage";
import QuizPage from "@/pages/QuizPage";
import FlashcardsPage from "@/pages/FlashcardsPage";
import LibraryPage from "@/pages/LibraryPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminDashboard from "@/pages/AdminDashboard";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ManageSubscriptionPage from "@/pages/ManageSubscriptionPage";
import ParentDashboard from "@/pages/ParentDashboard";
import ContentGallery from "@/pages/ContentGallery";
import SupportStaffDashboard from "@/pages/SupportStaffDashboard";
import LandingPage from "@/pages/LandingPage";
import SEOSubjectPage from "@/pages/SEOSubjectPage";
import SEOLearnHub from "@/pages/SEOLearnHub";
import LegalPage from "@/pages/LegalPage";
import ReferAndEarn from "@/pages/ReferAndEarn";
import GamifyPage from "@/pages/GamifyPage";
import PuzzlePlayPage from "@/pages/PuzzlePlayPage";
import MyGurukulPage from "@/pages/MyGurukulPage";
import NotesPage from "@/pages/NotesPage";
import MyVedaCoinsPage from "@/pages/MyVedaCoinsPage";
import ExamsPage from "@/pages/premium/ExamsPage";
import PlannerPage from "@/pages/premium/PlannerPage";
import CockpitPage from "@/pages/premium/CockpitPage";
import QuestionBankPage from "@/pages/premium/QuestionBankPage";
import WorksheetsPage from "@/pages/premium/WorksheetsPage";
import SummaryPage from "@/pages/premium/SummaryPage";
import SlidesPage from "@/pages/premium/SlidesPage";
import MockExamPage from "@/pages/premium/MockExamPage";
import SyllabusPage from "@/pages/SyllabusPage";
import GrowthPage from "@/pages/premium/GrowthPage";
import ParentInsightPage from "@/pages/premium/ParentInsightPage";
import WebLayout from "@/components/WebLayout";
import PaywallGuard from "@/components/PaywallGuard";
import { TutorBuddyProvider } from "@/components/TutorBuddy";
import { Toaster } from "@/components/ui/sonner";
import { captureRefFromUrl } from "@/lib/referral";
import { apiGet } from "@/lib/api";

function ProtectedRoute({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Setup is only escapable towards the pages the setup itself points at —
  // the Mode Of Study step links a Growth student to Premium.
  const ONBOARDING_ALLOWED = ['/home', '/subscription', '/subscription/manage'];
  if (requireOnboarding && !user.onboarding_complete && !ONBOARDING_ALLOWED.includes(location.pathname)) return <Navigate to="/home" replace />;
  return children;
}

function WebLayoutWrapper({ children }) {
  const [activeSubject, setActiveSubject] = useState('');
  return (
    <WebLayout activeSubject={activeSubject} setActiveSubject={setActiveSubject}>
      {({ chatResetKey, activeTopic, onSessionLoad }) => (
        typeof children === 'function' ? children({ activeSubject, setActiveSubject, chatResetKey, activeTopic, onSessionLoad }) : children
      )}
    </WebLayout>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // Phase 2.42 — content protection layer. Activates for every non-admin /
  // non-support_staff signed-in user. CSS-driven; the hook just toggles
  // <html data-gv-protected>. See useContentProtection.js for the trade-offs
  // (browsers cannot actually block OS-level screenshots; we get ~95% of the
  // way there + watermark / blur defences for the rest).
  useContentProtection(user);

  // Capture ?ref=CODE from the URL once on mount, regardless of which page the
  // friend lands on. AuthCallback replays it during OAuth so attribution survives.
  useEffect(() => {
    captureRefFromUrl();
  }, []);

  // Only marketing / SEO / legal routes should be indexable.
  // Everything else (app/auth) gets a noindex,nofollow meta tag injected dynamically.
  useEffect(() => {
    const p = location.pathname || '/';
    const publicIndexable =
      p === '/' ||
      p.startsWith('/learn') ||
      p === '/terms' || p === '/privacy' || p === '/refund' ||
      p === '/shipping' || p === '/contact';

    let tag = document.head.querySelector('meta[name="robots"]');
    if (!publicIndexable) {
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'robots');
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', 'noindex, nofollow');
    } else if (tag) {
      // Explicitly allow indexing for public routes
      tag.setAttribute('content', 'index, follow');
    }
  }, [location.pathname]);

  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Navigate to="/home" replace /></ProtectedRoute>} />

      {/* Web Layout routes */}
      <Route path="/home" element={
        <ProtectedRoute>
          <WebLayoutWrapper>
            {({ activeSubject, chatResetKey, activeTopic, onSessionLoad }) => <HomePage activeSubject={activeSubject} chatResetKey={chatResetKey} activeTopic={activeTopic} onSessionLoad={onSessionLoad} />}
          </WebLayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><WebLayoutWrapper><Dashboard /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/school-materials" element={<ProtectedRoute><WebLayoutWrapper><SchoolMaterialsPage /></WebLayoutWrapper></ProtectedRoute>} />
      <Route path="/notes" element={
        <ProtectedRoute><WebLayoutWrapper><NotesPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/my-gurukul" element={
        <ProtectedRoute><WebLayoutWrapper><MyGurukulPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/my-veda-coins" element={
        <ProtectedRoute><WebLayoutWrapper><MyVedaCoinsPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <WebLayoutWrapper>
            {({ activeSubject, chatResetKey, activeTopic, onSessionLoad }) => <HomePage activeSubject={activeSubject} chatResetKey={chatResetKey} activeTopic={activeTopic} onSessionLoad={onSessionLoad} />}
          </WebLayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/chat/:sessionId" element={
        <ProtectedRoute><WebLayoutWrapper><ChatPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/quiz" element={
        <ProtectedRoute><WebLayoutWrapper><QuizPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/flashcards" element={
        <ProtectedRoute><WebLayoutWrapper><FlashcardsPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute><WebLayoutWrapper><LibraryPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><WebLayoutWrapper><ProfilePage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute><WebLayoutWrapper><SubscriptionPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/subscription/manage" element={
        <ProtectedRoute><WebLayoutWrapper><ManageSubscriptionPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/refer" element={
        <ProtectedRoute><WebLayoutWrapper><ReferAndEarn /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/gamify" element={
        <ProtectedRoute><WebLayoutWrapper><GamifyPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/gamify/play/:puzzleId" element={
        <ProtectedRoute><WebLayoutWrapper><PuzzlePlayPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/exams" element={
        <ProtectedRoute><WebLayoutWrapper><ExamsPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/planner" element={
        <ProtectedRoute><WebLayoutWrapper><PlannerPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/cockpit" element={
        <ProtectedRoute><WebLayoutWrapper><CockpitPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/question-bank" element={
        <ProtectedRoute><WebLayoutWrapper><QuestionBankPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/worksheets" element={
        <ProtectedRoute><WebLayoutWrapper><WorksheetsPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/summary" element={
        <ProtectedRoute><WebLayoutWrapper><SummaryPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/slides" element={
        <ProtectedRoute><WebLayoutWrapper><SlidesPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/mock-exam" element={
        <ProtectedRoute><WebLayoutWrapper><MockExamPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/syllabus" element={
        <ProtectedRoute><WebLayoutWrapper><SyllabusPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/growth" element={
        <ProtectedRoute><WebLayoutWrapper><GrowthPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/parent-insight" element={
        <ProtectedRoute><WebLayoutWrapper><ParentInsightPage /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/parent" element={
        <ProtectedRoute><WebLayoutWrapper><ParentDashboard /></WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/gallery" element={
        <ProtectedRoute><WebLayoutWrapper>
          {({ activeSubject, activeTopic }) => <ContentGallery activeSubject={activeSubject} activeTopic={activeTopic} />}
        </WebLayoutWrapper></ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute><SupportStaffDashboard /></ProtectedRoute>
      } />
      <Route path="/support" element={<Navigate to={`/staff${location.search}`} replace />} />

      <Route path="/" element={<LandingPage />} />
      <Route path="/learn" element={<SEOLearnHub />} />
      <Route path="/learn/:subjectSlug/:classSlug" element={<SEOSubjectPage />} />
      <Route path="/learn/:boardSlug/:subjectSlug/:classSlug" element={<SEOSubjectPage />} />

      {/* Legal pages — public, indexable, required by payment gateways */}
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/refund" element={<LegalPage />} />
      <Route path="/shipping" element={<LegalPage />} />
      <Route path="/contact" element={<LegalPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [buddyEnabled, setBuddyEnabled] = useState(true);
  useEffect(() => {
    apiGet('/public/settings').then(d => {
      const t = d?.feature_toggles?.tutor_buddy_enabled;
      if (t !== undefined) setBuddyEnabled(!!t);
    }).catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <PaywallGuard>
          <TutorBuddyProvider enabled={buddyEnabled}>
            <AppRouter />
          </TutorBuddyProvider>
        </PaywallGuard>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
